import {
  Autocomplete,
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  InputLabel,
  Stack,
  TextField
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useEffect } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';

import CheckPermissions from '@/components/CheckPermissions';
import { PERMISSIONS } from '@/constants/auth.constants';
import { useFetchSettings } from '@/pages/Administration/Administration.store';

import useStyles from './NotificationSettings.styles';
import type { NotificationSettingsFormProps } from './NotificationSettings.types';

let oldValues: Record<string, boolean> = {};

const NotificationSettingsForm = (props: NotificationSettingsFormProps) => {
  const { isEditing = true, permissionsCheck = false } = props;
  const { classes } = useStyles();
  const theme = useTheme();
  const { data: settings } = useFetchSettings();

  const checkboxIndex = Array.from(Array(5).keys());

  //-----------------------------------------------------------------
  const { control, setValue, getValues, setError, clearErrors } =
    useFormContext();

  //-----------------------------------------------------------------
  const priorityValues = useWatch({
    control,
    name: checkboxIndex.map(
      (index) => `notification_ticket_threat_priority_p${index + 1}`
    )
  });

  const threatValue = useWatch({ control, name: 'notification_ticket_threat' });
  const reportEmailValue = useWatch({
    control,
    name: 'notification_report_email'
  });

  useEffect(() => {
    const anyPriorityChecked = priorityValues.some(Boolean);
    const allPriorityUnchecked = priorityValues.every((value) => !value);
    const oldThreat = oldValues.notification_ticket_threat;
    const newThreat = getValues('notification_ticket_threat');

    // Handle checkbox logic based on different scenarios
    switch (true) {
      // if ticket threat checkbox gets toggled, check all priority checkboxes
      case !oldThreat && newThreat && !anyPriorityChecked:
        checkboxIndex.forEach((index) => {
          setValue(`notification_ticket_threat_priority_p${index + 1}`, true);
        });
        break;
      // if ticket threat checkbox gets unchecked, uncheck all priority checkboxes
      case oldThreat && !newThreat && anyPriorityChecked:
        checkboxIndex.forEach((index) => {
          setValue(`notification_ticket_threat_priority_p${index + 1}`, false);
        });
        break;
      // if ticket threat checkbox is unchecked and at least one priority checkbox is checked, check the ticket threat checkbox
      case !oldThreat && !newThreat && anyPriorityChecked:
        setValue('notification_ticket_threat', true);
        break;
      // if ticket threat checkbox is checked and all priority checkboxes are unchecked, uncheck the ticket threat checkbox
      case oldThreat && newThreat && allPriorityUnchecked:
        setValue('notification_ticket_threat', false);
        break;
      default:
        // No action needed for other scenarios
        break;
    }
    oldValues = getValues();
  }, [getValues(), threatValue]);

  // check if at least one ticket event is selected when threat or service ticket is checked
  const validateEvents = () => {
    const threatChecked = getValues('notification_ticket_threat');
    const serviceChecked = getValues('notification_ticket_service');
    const createChecked = getValues('notification_ticket_event_create');
    const updateChecked = getValues('notification_ticket_event_update');

    if (!createChecked && !updateChecked && (threatChecked || serviceChecked)) {
      setError('notification_ticket_event', {
        type: 'manual',
        message: 'At least one ticket event must be selected.'
      });
      return false;
    }

    clearErrors('notification_ticket_event');
    clearErrors('notification_ticket_event_create');
    clearErrors('notification_ticket_event_update');
    return true;
  };

  return (
    <Stack spacing={3}>
      <Box className={classes.cardContentBox}>
        <Controller
          control={control}
          name="notification_ticket_threat"
          render={({ field }) => (
            <FormControlLabel
              control={
                <Checkbox
                  checked={field.value}
                  disabled={permissionsCheck || !isEditing}
                  {...field}
                />
              }
              label={
                <>
                  <strong>THREAT TICKET</strong>: Send notification for threat
                  ticket with a priority of ...
                </>
              }
            />
          )}
        />
        <FormGroup row sx={{ ml: 4 }}>
          {checkboxIndex.map((index) => (
            <Controller
              key={index}
              control={control}
              name={`notification_ticket_threat_priority_p${index + 1}`}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={field.value}
                      disabled={permissionsCheck || !isEditing}
                      {...field}
                    />
                  }
                  label={`P${index + 1}`}
                />
              )}
            />
          ))}
        </FormGroup>
      </Box>
      <Box className={classes.cardContentBox}>
        <Controller
          control={control}
          name="notification_ticket_service"
          render={({ field }) => (
            <FormControlLabel
              control={
                <Checkbox
                  checked={field.value}
                  disabled={permissionsCheck || !isEditing}
                  {...field}
                />
              }
              label={
                <>
                  <strong>SERVICE TICKET</strong>: Send notification for service
                  tickets
                </>
              }
            />
          )}
        />
      </Box>
      <Box className={classes.cardContentBox}>
        <Controller
          control={control}
          name="notification_announcement"
          render={({ field }) => (
            <FormControlLabel
              control={
                <Checkbox
                  checked={field.value}
                  disabled={permissionsCheck || !isEditing}
                  {...field}
                />
              }
              label={
                <>
                  <strong>ANNOUNCEMENT</strong>: Send notification when a new
                  announcement is posted
                </>
              }
            />
          )}
        />
      </Box>
      <Box className={classes.cardContentBox}>
        <Controller
          control={control}
          name="notification_report_email"
          render={({ field }) => (
            <FormControlLabel
              control={
                <Checkbox
                  checked={field.value}
                  disabled={permissionsCheck || !isEditing}
                  {...field}
                />
              }
              label={
                <>
                  <strong>REPORT EMAIL</strong>: Send email notifications when
                  reports are created
                </>
              }
            />
          )}
        />
        {reportEmailValue && (
          <Box sx={{ ml: 4 }}>
            <Controller
              control={control}
              name="notification_report_attachment"
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={field.value}
                      disabled={permissionsCheck || !isEditing}
                      {...field}
                    />
                  }
                  label={
                    <>
                      <strong>Include Attachments</strong>: Include the report
                      PDF as an email attachment when possible
                    </>
                  }
                />
              )}
            />
          </Box>
        )}
      </Box>
      <CheckPermissions permissions={PERMISSIONS.INTERNAL}>
        {settings?.tags?.[0] && (
          <Box
            sx={{ mt: 0, width: '60%', minWidth: '20rem' }}
            className={classes.cardContentBox}
          >
            <InputLabel sx={{ my: 1 }}>Ticket Tags</InputLabel>
            <Controller
              control={control}
              name="notification_tags"
              render={({ field: { onChange, value } }) => (
                <Autocomplete
                  multiple
                  options={settings?.tags || []}
                  value={value || []}
                  onChange={(_e, newValue) => onChange(newValue)}
                  disabled={permissionsCheck || !isEditing}
                  renderInput={(params) => (
                    <TextField {...params} variant="outlined" />
                  )}
                />
              )}
            />
            <FormHelperText>
              Send a notification only when a ticket includes any of these tags.
              Leave empty to disable filtering by tags.
            </FormHelperText>
          </Box>
        )}
      </CheckPermissions>
      <Box className={classes.cardContentBox} sx={{ pt: 2 }}>
        <InputLabel>Ticket Events</InputLabel>
        <FormGroup>
          <Controller
            control={control}
            rules={{ validate: validateEvents }}
            name="notification_ticket_event_create"
            render={({ field, formState }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={field.value || false}
                    disabled={permissionsCheck || !isEditing}
                    sx={{
                      color:
                        formState.errors.notification_ticket_event &&
                        theme.vars.palette.error.main
                    }}
                    {...field}
                  />
                }
                label={
                  <>
                    <strong>CREATE</strong>: Send notification when a ticket is
                    created
                  </>
                }
              />
            )}
          />
          <Controller
            control={control}
            rules={{ validate: validateEvents }}
            name="notification_ticket_event_update"
            render={({ field, formState }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={field.value || false}
                    disabled={permissionsCheck || !isEditing}
                    sx={{
                      color:
                        formState.errors.notification_ticket_event &&
                        theme.vars.palette.error.main
                    }}
                    {...field}
                  />
                }
                label={
                  <>
                    <strong>UPDATE</strong>: Send notification when a ticket is
                    updated
                  </>
                }
              />
            )}
          />
        </FormGroup>
      </Box>
    </Stack>
  );
};

export default NotificationSettingsForm;
